import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Empty } from 'antd';

import { selectors } from 'state';
import { formatters } from 'services';

const BenchmarkAndFundPerformance: React.FC = () => {
  const players = useSelector(selectors.activePlayers);

  const hasPlayers = !!players?.length;
  const categories = useMemo(() => (players ?? []).map(p => p.name), [players]);

  const fundData = useMemo(
    () => (players ?? []).map(p => p.last_portfolio_return ?? 0),
    [players]
  );
  // TODO: busted players' metrics will stop at time of bust, hence will not have the latest benchmark.
  // TODO: this should apply the longest existing benchmark data to everybody
  const benchmarkData = useMemo(() => {
    return (players ?? []).map(p => {
      const last = p.metrics?.[p.metrics.length - 1] || {};
      return (last as any).market_index_return ?? 0;
    });
  }, [players]);

  const single = categories.length === 1;

  const series = useMemo(() => {
    // Apex uses one chart type; we emulate two column series with different colors.
    // Benchmark "fillOpacity" isn’t meaningful for columns in Apex; we approximate with lighter color.
    return [
      { name: 'Fund', data: fundData },
      { name: 'Benchmark', data: benchmarkData },
    ];
  }, [fundData, benchmarkData]);

  const options = useMemo((): ApexOptions => {
    return {
      chart: {
        type: 'bar',
        // height: '1%',
        animations: { enabled: false },
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: single ? '40px' : '55%', // ensures a visible bar when only one category
          // borderRadius: 0,
        },
      },
      stroke: {
        show: false,
      },
      colors: ['#34a2f9', '#aaaaaa'],
      dataLabels: { enabled: false },
      legend: { show: true },
      xaxis: {
        categories,
        labels: {
          style: { fontSize: '12px' },
        },
        tickPlacement: single ? 'on' : 'between', // closest analogue to Highcharts tickmarkPlacement
      },
      yaxis: {
        labels: {
          formatter: (v: number) => formatters.percent2Digit(v),
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex }) => {
          const idx = dataPointIndex ?? 0;
          const name = categories[idx] ?? '';
          const fmt = (v: number) => formatters.percent2Digit(v);

          return `<div style="padding:8px 10px;">
            <b>${name}</b><br/>
            <span style="color:#34a2f9">●</span> Fund: <b>${fmt(
              fundData[idx] ?? 0
            )}</b><br/>
            <span style="color:#aaaaaa">●</span> Benchmark: <b>${fmt(
              benchmarkData[idx] ?? 0
            )}</b>
          </div>`;
        },
      },
      grid: {
        padding: { left: 8, right: 8 },
      },
    };
  }, [categories, fundData, benchmarkData, single]);

  if (!hasPlayers) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <Chart
      options={options}
      series={series as any}
      type="bar"
      height="56.25%"
    />
  );
};

export default BenchmarkAndFundPerformance;
